/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: LoginPage.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Modern login page with glassmorphism design, dynamic branding,
    and comprehensive error handling.

 🔧 Notes:
    - Implements modern glassmorphism design similar to FreeSWITCH
    - Supports dynamic branding based on domain
    - Includes animated background elements
    - Responsive design with backdrop blur effects
    - Integrates with authentication store and branding system

 🔁 Last Updated: 2025-01-06 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2, User, Lock, ServerCrash, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../hooks/useI18n';
import { useBranding } from '../hooks/useBranding';
import { toast } from 'sonner';

const loginSchema = z.object({
  identifier: z.string().min(1, 'Username or email is required'),
  password: z.string().min(1, 'Password is required')
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error } = useAuthStore();
  const { t } = useI18n();
  const { brandingData, isLoading: brandingLoading, error: brandingError } = useBranding();
  const [showPassword, setShowPassword] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  // Check if setup is needed on mount
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await fetch('/api/auth/setup-status');
        const data = await response.json();

        if (data.needsSetup) {
          navigate('/setup');
          return;
        }
      } catch (err) {
        console.error('Failed to check setup status:', err);
      } finally {
        setCheckingSetup(false);
      }
    };

    checkSetupStatus();
  }, [navigate]);

  /* 
   * BRANDING FIELD MAPPINGS:
   * 
   * 1. Login Image Background → loginBackgroundImage (Login Design tab)
   * 2. Login Logo → loginLogo (Login Design tab)
   * 
   * Additional branding fields used:
   * - appName → Page title and logo fallback
   * - welcomeMessage → Main heading
   * - footerText → Footer text
   * - primaryColor → Logo background color (fallback)
   * - loginBackgroundColor → Page background color
   * - loginFontColor → Text color
   * - loginFormBackground → Form background color
   * - loginSubmitButtonColor → Submit button color
   * - disableLoginBackgroundImage → Toggle background image
   */

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema)
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      await login(data.identifier, data.password);
      toast.success(t('auth.loginSuccess'));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(error.message || t('auth.invalidCredentials'));
    }
  };

  // Helper function to convert hex (including 8-digit with alpha) to rgba
  const hexToRgba = (hex: string): string => {
    if (!hex) return hex;
    hex = hex.replace(/^#/, '');

    let r = 0, g = 0, b = 0, a = 1;

    if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (hex.length === 8) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
      a = parseInt(hex.substring(6, 8), 16) / 255;
    } else {
      return `#${hex}`; // Return as-is if not 6 or 8 digits
    }

    return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
  };

  // Get branding values with fallbacks
  const branding = brandingData?.brandingConfig;
  const appName = branding?.appName || 'Ring2All Platform';
  const welcomeMessage = branding?.welcomeMessage || t('auth.welcome');
  const primaryColor = branding?.primaryColor || '#3B82F6';
  const loginBackgroundColor = branding?.loginBackgroundColor || '#ffffff';
  const loginFontColor = branding?.loginFontColor || '#ffffff';

  // Convert colors with transparency to rgba for better browser support
  const loginFormBackground = hexToRgba(branding?.loginFormBackground || '#4448476B');
  const loginSubmitButtonColor = hexToRgba(branding?.loginSubmitButtonColor || '#202C2C7D');
  const loginAddonsMenuColor = hexToRgba(branding?.loginAddonsMenuColor || '#3B82F633');

  // Login Design fields - these are the correct mappings
  const loginBackgroundImage = branding?.loginBackgroundImage; // 1. Login Image Background --> Background Image
  const loginLogo = branding?.loginLogo; // 2. Login Logo --> Tab Login Design (Logo)
  const disableLoginBackgroundImage = branding?.disableLoginBackgroundImage || false;

  // Show loading state while branding is loading
  if (brandingLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/70 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Show Service Unavailable screen if the API is completely unreachable
  if (brandingError && !brandingLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="w-full max-w-md relative z-10">
          <div
            className="relative backdrop-blur-xl border border-red-500/20 rounded-2xl p-8 shadow-2xl text-center"
            style={{
              backgroundColor: 'rgba(30, 41, 59, 0.7)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(239, 68, 68, 0.1)'
            }}
          >
            <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-6 border border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.3)]">
              <ServerCrash className="h-10 w-10 text-red-400" />
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">Service Unavailable</h1>
            <p className="text-slate-300 mb-8 leading-relaxed">
              We're currently unable to connect to the Ring2All platform services. The system might be undergoing maintenance or experiencing a network issue.
            </p>

            <button
              onClick={() => window.location.reload()}
              className="w-full py-4 px-6 rounded-xl font-bold text-lg text-white transition-all duration-300 transform hover:scale-105 active:scale-95 group relative overflow-hidden bg-gradient-to-r from-red-600 to-orange-600 hover:shadow-[0_0_20px_rgba(239,68,68,0.4)]"
            >
              <div className="flex items-center justify-center relative z-10">
                <RefreshCw className="h-5 w-5 mr-2 group-hover:rotate-180 transition-transform duration-500" />
                <span>Try Again</span>
              </div>
            </button>
            
            <div className="mt-8 pt-6 border-t border-slate-700/50">
              <p className="text-sm text-slate-400">
                If the problem persists, please contact your system administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="login-page min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
      style={{
        backgroundColor: loginBackgroundColor,
        // 1. Login Image Background --> Background Image (from Login Design tab)
        backgroundImage: !disableLoginBackgroundImage && loginBackgroundImage
          ? `url(${loginBackgroundImage})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Background overlay with gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: !disableLoginBackgroundImage && loginBackgroundImage
            ? 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)'
            : 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)'
        }}
      />

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Login Form with Glassmorphism */}
        <div
          className="relative backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl"
          style={{
            backgroundColor: loginFormBackground,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)'
          }}
        >
          {/* Logo Section - Full Width */}
          <div className="text-center mb-8">
            {/* Priority: 1. loginLogo (Login Design tab), 2. logo (Logos & Icons tab), 3. Initial */}
            {loginLogo ? (
              <div className="w-full mb-8 flex items-center justify-center">
                <img
                  src={loginLogo}
                  alt={appName}
                  className="w-full h-24 object-contain drop-shadow-lg"
                  title={`${appName} - Login Logo`}
                />
              </div>
            ) : branding?.logo ? (
              <div className="w-full mb-8 flex items-center justify-center">
                <img
                  src={branding.logo}
                  alt={appName}
                  className="w-full h-24 object-contain drop-shadow-lg"
                  title={`${appName} - Main Logo`}
                />
              </div>
            ) : (
              <div
                className="w-full h-24 rounded-2xl flex items-center justify-center mb-8 shadow-lg"
                style={{
                  backgroundColor: primaryColor,
                  boxShadow: `0 0 30px ${primaryColor}40`
                }}
                title={`${appName} - Initial`}
              >
                <span className="text-6xl font-bold text-white">
                  {appName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-white/70" />
                </div>
                <input
                  {...register('identifier')}
                  type="text"
                  id="identifier"
                  className="w-full pl-10 pr-4 py-4 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300"
                  placeholder="Username or email"
                  style={{
                    background: 'rgba(0, 0, 0, 0) !important',
                    backgroundColor: 'transparent !important',
                    backdropFilter: 'blur(8px)',
                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                    WebkitBackdropFilter: 'blur(8px)'
                  }}
                />
              </div>
              {errors.identifier && (
                <p className="text-red-400 text-sm mt-1">{errors.identifier.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-white/70" />
                </div>
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="w-full pl-10 pr-12 py-4 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300"
                  placeholder="••••••••"
                  style={{
                    background: 'rgba(0, 0, 0, 0) !important',
                    backgroundColor: 'transparent !important',
                    backdropFilter: 'blur(8px)',
                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                    WebkitBackdropFilter: 'blur(8px)'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/60 hover:text-white/80 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-xl backdrop-blur-sm">
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 px-6 rounded-xl font-bold text-xl text-white focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 hover:shadow-2xl active:scale-95 group relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${loginSubmitButtonColor} 0%, ${loginSubmitButtonColor}dd 100%)`,
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            >
              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform -skew-x-12 -translate-x-full group-hover:translate-x-full"></div>

              {isLoading ? (
                <div className="flex items-center justify-center relative z-10">
                  <Loader2 className="h-6 w-6 animate-spin mr-3" />
                  <span className="text-xl font-bold">Logging in...</span>
                </div>
              ) : (
                <span className="relative z-10 text-xl font-bold">Login</span>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div
          className="text-center mt-8 text-sm"
          style={{
            color: loginFontColor,
            opacity: 0.7,
            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
          }}
        >
          <p>{branding?.footerText || 'Ring2All Platform v1.0.0'}</p>
        </div>
      </div>
    </div>
  );
}
